import React, { useState, useEffect, useCallback } from 'react';

import useForm from '@utils/useForm';
import { isNil, isEmpty, get, find, set } from 'lodash';
import { useHistory } from "react-router-dom";
import { useTheme, ITheme } from 'src/theme';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';
import { styled } from '@linaria/react';

import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { updateSQSQuotation, updateCoverage, updateQuoteResponse, updateQuoteRequest, updatePromoCode, completeStep, updateTransactionId } from '@redux/slices/SIO/step1Slice';
import { updateDeclaration, updateReadDocs, completeStep as completeStep4 } from '@redux/slices/SIO/step4Slice';
import { CoverageForm } from '@models/SIO/Coverage.model';
import { SQSPocketRequest, LifeProfile, CoverageAttribute, Coverages, SQSPocketResponse } from '@models/SQSPocketRequest.model';
import { postComputePremium } from '@api/index';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import Button from '@components/Button';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import StickyBox from '@components/StickyBox';
import Text from '@components/Text';
import Input from '@components/Input';
import Dropdown from '@components/Dropdown';
import ContainerBlock from '@components/ContainerBlock';
import TextCollapsible from '@components/TextCollapsible';
import ContactBox from '@components/ContactBox';
import Divider from '@components/Divider';
import BulletList from '@components/BulletList';
import Bullet from '@components/Bullet';
import BeforeProcessWarning from '@components/BeforeProcessWarning';
import Quote from '@components/Quote';
import PremiumCost from '@components/PremiumCost';
import PolicyDocument from '@components/PolicyDocument';
import Modal from '@components/Modal';

import { validate } from './service';
import { setIsGlobalError, setLoadingState } from '@redux/slices/loadingSlice';
import { fileLinkGen } from '@static/staticConstents';

const FlexBox = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 40px;
  @media screen and (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    div {
      margin: 0;
      margin-right: 20px;
      input {
        min-width: 370px
      }
    }
    button {
      width: 136px !important;
      box-sizing: content-box;
      letter-spacing: -0.5px;
    }
  }
  @media screen and (min-width: 960px) {
    div {
      input {
        min-width: 444px;
      }
    }
    
  }
`

const page = 'Coverage';

const quoteInitialSample = [
  {
    name: 'Basic plan',
    type: 'basic',
    details: [
      { text: 'Plan', value: '-' },
      { text: 'Sum assured', value: '-' },
      { text: 'Premium payment term', value: 'Yearly renewable' },
      { text: 'Premium amount', value: '-' },
    ]
  },
  {
    name: 'Add-ons',
    type: 'add-on',
    details: [
      { text: 'Rider', value: '-' },
      { text: 'Sum assured', value: '-' },
      { text: 'Premium payment term', value: 'Yearly renewable' },
      { text: 'Premium amount', value: '-' },
    ]
  },
  // {
  //   type: 'add-on-pa',
  //   details: [
  //     { text: 'Rider', value: '-' },
  //     { text: 'Sum assured', value: '-' },
  //     { text: 'Premium payment term', value: 'Yearly renewable' },
  //     { text: 'Premium amount', value: '-' },
  //   ]
  // },
  {
    name: 'Other details',
    type: 'others',
    details: [
      { text: 'Premium payment frequency', value: 'Yearly' },
      { text: 'Promo code', value: '-' },
    ]
  }
]

const createSQSPayload = ({ basicDetails, coverage, promoCodeSummary, transactionId }: any): SQSPocketRequest => {
  const { day, month, year } = basicDetails.dob;
  const stringDate = `${month}/${day}/${year}`;
  const age = Helpers.getAge(new Date(stringDate));
  const lifeProfile = new LifeProfile({
    ...basicDetails,
    dob: `${day}${month}${year}`,
    age
  });
  const { nationalityCode } = basicDetails;
  const { promoCode, illnessCover, accidentCover, sumAssured, ...orders } = coverage;
  const attributePlan = new CoverageAttribute({ ...orders, nationalityCode, sumAssured: Number(sumAssured), promoCodeSummary });
  const coveragePlan = new Coverages({ id: 'simpleTermPlan', type: 'P', attributes: attributePlan });
  let payload = new SQSPocketRequest({
    lifeprofiles: [lifeProfile],
    coverages: [coveragePlan],
    transactionId,
  });
  if (!isEmpty(illnessCover)) {
    const attributeIllnessCover = { sumAssured: Number(illnessCover) };
    const coverageIllnessCover = new Coverages({ id: 'lateStageAddCICover', type: 'R', attributes: attributeIllnessCover });
    console.log(coverageIllnessCover);

    payload.coverages.push(coverageIllnessCover);
  }

  if (!isEmpty(accidentCover)) {
    const attributeAccidentCover = { sumAssured: Number(accidentCover) };
    const coverageAccidentCover = new Coverages({ id: 'personalAccidentRider', type: 'R', attributes: attributeAccidentCover });
    payload.coverages.push(coverageAccidentCover);
  }

  return payload;

}


const trackOnload = (promocode: string, premium: SQSPocketResponse | null): void => {
  const GA = GAAviva.getInstance();
  let { acquisition, form } = GA.digitalData;
  form.name = 'Coverage';
  let { insurance, product } = acquisition;
  product = { ...product, prodID: 'STP' };
  if (premium) {
    product.prodName = premium.name;
    insurance = {
      ...insurance,
      promocode,
      campaignCode: promocode,
      premium: `${premium.totalAnnualizedPremium}`,
      premiumTerm: 'Yearly renewable',
      sumassured: `${premium.sumAssured}`,
      addOns: premium.sioAddon
    }
  }
  GA.trackPageLoad({ acquisition: { ...acquisition, insurance, product } });
}

const trackFormSubmmit = (error: string = ''): void => {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}


export interface CoverageProps { }

function Coverage(props: CoverageProps) {
  console.log(`Aviva - ${page}`);
  const theme: ITheme = useTheme();
  const history = useHistory();
  const urlConfig = get(window, 'AVIVA_VARIABLE_CONFIG');
  const insuranceEstimatorUrl = get(urlConfig, 'insuranceEstimatorUrl');
  const budgetCalculatorUrl = get(urlConfig, 'budgetCalculatorUrl');;
  const compareFirstUrl = get(urlConfig, 'compareFirstUrl');;;

  const basicDetails = useAvivaSelector(state => state.sioStep1.basicDetails);
  const initValue = useAvivaSelector(state => state.sioStep1.coverage);
  const selectedOccupation = useAvivaSelector(state => state.sioStep1.occupation);
  const appliedPromoCode = useAvivaSelector(state => state.sioStep1.appliedPromoCode);
  const quoteRawResponse = useAvivaSelector(state => state.sioStep1.quoteRawResponse);
  const hasMyInfo = useAvivaSelector(state => state.sioStep2.hasMyInfo);
  const transactionId = useAvivaSelector(state=>state.sioStep1.transactionId)


  const paymentFrequency = useAvivaSelector(state => state.staticData.paymentFrequency);
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);

  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [premium, setPremium] = useState<any>({});
  const [quotes, setQuotes] = useState<any>([]);
  const [productSummary, setProductSummary] = useState<string>('');

  const [needToCalculate, setNeedToCalculate] = useState<boolean>(false);
  const [isCalculated, setIsCalculated] = useState<boolean>(false);
  const [isAppliedPromoCode, setAppliedPromoCode] = useState<boolean>(false);
  const [checkedPromoCode, setCheckedPromoCode] = useState<string>('');
  const [isShowConfirm, setIsShowConfirm] = useState<boolean>(false);
  const [promoCodeError, setPromoCodeError] = useState<string>('');
  const [appliedDiscounts, setAppliedDiscounts] = useState<string[]>([]);

  const { handleChange, values, errors, formValid, patchValue } = useForm(new CoverageForm(initValue), () => { }, validate);

  const dispatch = useAvivaDispatch();

  const toggleModal = () => { setIsShowConfirm(!isShowConfirm); };

  const gotoContact = (ulnerable: boolean = false) => { history.push(`/sio/contact?ulnerable=${ulnerable}`) };

  const onBack = useCallback(() => { history.push('/sio/basic-details') }, [history]);
  const onContinue = () => {
    if (!isAppliedPromoCode && selectedOccupation?.status === 'Decline') {
      gotoContact();
    } else {
      dispatch(completeStep(true));
      dispatch(updateCoverage(values));
      console.debug('checkedPromoCode', checkedPromoCode);
      dispatch(updatePromoCode(checkedPromoCode));
      if (!hasMyInfo) {
        history.push('/sio/my-info')
      } else {
        history.push('/sio/my-info?step=1')
      }

    }
  };


  const getProductSummary = useCallback(() => {
    const isCIRider = values['illnessCover'] > 0;
    const isPARider = values['accidentCover'] > 0;
    if (isCIRider && isPARider) {
      setProductSummary(fileLinkGen('sio',"DirectsalesSIO-PS-CI-PA.pdf"));
    } else if (isCIRider) {
      setProductSummary(fileLinkGen("sio", "DirectsalesSIO-PS-CI.pdf"));
    } else if (isPARider) {
      setProductSummary(fileLinkGen('sio',"DirectsalesSIO-PS-PA.pdf"));
    } else {
      setProductSummary(fileLinkGen('sio', "DirectsalesSIO-PS.pdf"));
    }
  }, [values])

  const setCalculatedPremium = useCallback((premium: SQSPocketResponse) => {
    const quoteValue = premium.sioQuote;
    const premiumValue = premium.sioPremium;
    setQuotes(quoteValue);
    setPremium(premiumValue);
    console.log(premiumValue);
    getProductSummary();
    setIsCalculated(true);
    setNeedToCalculate(false);
    dispatch(updateDeclaration(null));
    dispatch(updateReadDocs(null));
    dispatch(completeStep4(false));
    trackOnload(checkedPromoCode, premium);
  }, [setQuotes, setPremium, setIsCalculated, dispatch, getProductSummary, checkedPromoCode]);

  const onCalculatePremium = async () => {
    if (!basicDetails) {
      onBack();
      return;
    }
    if (!isEmpty(values['promoCode'].toUpperCase())) {
      await onCheckPromoCode();
    }

    dispatch(setLoadingState(true));
    let newTransactionId = transactionId;
    if (!transactionId) {
      newTransactionId = window.crypto.randomUUID();
      dispatch(updateTransactionId(newTransactionId));
    }

    const payload: SQSPocketRequest = createSQSPayload({ basicDetails, coverage: values, promoCodeSummary: checkedPromoCode, transactionId: newTransactionId });
    dispatch(updateQuoteRequest({ ...payload, document: ['cp', 'bi'] }));
    try {
      const response = await postComputePremium(payload);
      const data = get(response, 'data.data', {});
      const pocket = new SQSPocketResponse(data);
      pocket.isCIRider = values['illnessCover'] > 0;
      pocket.isPARider = values['accidentCover'] > 0;
      const discounts = pocket.discounts;
      setAppliedDiscounts(discounts);
      setCalculatedPremium(pocket);
      setPromoCodeError('');
      dispatch(updateSQSQuotation(pocket));
      dispatch(updateCoverage(values));
      dispatch(updateQuoteResponse(data));
      dispatch(setLoadingState(false));
      trackFormSubmmit();
      window.scrollTo(0, 0);
    } catch (error) {
      console.error(error);
      dispatch(setIsGlobalError(true));
      trackFormSubmmit(get(error, 'message'))
      dispatch(setLoadingState(false));
    }
  };

  const onCheckPromoCode = async () => {
    const promoCodeSummary = values['promoCode'].toUpperCase();
    if (!transactionId) {
      dispatch(updateTransactionId(crypto.randomUUID()));
    }
    const payload: SQSPocketRequest = createSQSPayload({ basicDetails, coverage: values, promoCodeSummary, transactionId });
    try {
      const response = await postComputePremium(payload);
      const data = get(response, 'data.data', {});
      const pocket = new SQSPocketResponse(data);
      const checkedCode = pocket.checkValidPromoCode(promoCodeSummary);
      const { valid, error } = checkedCode;
      setAppliedPromoCode(valid);
      setPromoCodeError(error);
      setCheckedPromoCode(valid ? promoCodeSummary : '');
      const footnotes = valid ? pocket.discounts : [];
      setAppliedDiscounts(footnotes);
      if (!isEmpty(promoCodeError)) {
        patchValue({ promoCode: '' });
      }
      trackFormSubmmit();
    } catch (error) {
      trackFormSubmmit(get(error, 'message'))
      setAppliedPromoCode(false);
    }
  };

  const onChange = (event: any) => {
    console.log(event);

    if (get(event, 'target.name', '') === 'promoCode' && isAppliedPromoCode) {
      setAppliedPromoCode(false);
      setAppliedDiscounts([]);
      setCheckedPromoCode('');
    }
    if (!needToCalculate) {
      setNeedToCalculate(true);
    }

    let newCI = {
      type: 'add-on',
      name: 'Add-ons',
      details: [
        { text: 'Rider', value: '-' },
        { text: 'Sum assured', value: '-' },
        { text: 'Premium payment term', value: 'Yearly renewable' },
        { text: 'Premium amount', value: '-' },
      ]
    };
    let newPA = {
      type: 'add-on-pa',
      details: [
        { text: 'Rider', value: '-' },
        { text: 'Sum assured', value: '-' },
        { text: 'Premium payment term', value: 'Yearly renewable' },
        { text: 'Premium amount', value: '-' },
      ]
    };
    let isCICover = (values['illnessCover'] > 0);
    let isPACover = (values['accidentCover'] > 0);

    handleChange(event);
    const { name, value } = event.target;
    console.log({ name, value, empty: isEmpty(value) });



    if (name === 'illnessCover') {
      isCICover = !isEmpty(value);
    }
    if (name === 'accidentCover') {
      isPACover = !isEmpty(value);
    }


    
    let oneRider = true;
    const basicTarget = find(quotes, { type: 'basic' });
    const otherTarget = find(quotes, { type: 'others' });
    let ciTarget = find(quotes, { type: 'add-on' });
    let paTarget = find(quotes, { type: 'add-on-pa' });
    let newQuote = [basicTarget, { ...ciTarget }, { ...paTarget }, otherTarget];
    
    //console.log({ quotes });
    //console.log({ ciTarget, paTarget });
    if (isEmpty(premium)) {
      if (isCICover && isPACover) {
        oneRider = false;
        if (!paTarget) {
          //console.log('TEST 1', ciTarget['details'][0].value, { ...ciTarget });
          if (ciTarget['details'][0].value === 'Personal Accident Cover') {
            //console.log('TEST 2', ciTarget['details'][0].value, { ...ciTarget });
            paTarget = {
              type: 'add-on-pa',
              details: [
                { text: 'Rider', value: 'Personal Accident Cover' },
                { text: 'Sum assured', value: ciTarget['details'][1].value },
                { text: 'Premium payment term', value: ciTarget['details'][2].value },
                { text: 'Premium amount', value: '-' },
              ]
            };
            delete paTarget.name;
          } else {
            paTarget = newPA;
          }
        } else {
          delete paTarget.name;
        }
        if (!ciTarget) {
          ciTarget = newCI;
        }
        newQuote = [basicTarget, { ...ciTarget }, { ...paTarget }, otherTarget];
      } else {
        if (isCICover) {
          // console.log('TEST 1', ciTarget['details'][0].value, { ...ciTarget }, {valid: !ciTarget } );
          if (!ciTarget) {
            newCI.details[0].value = values['illnessCover'] ? 'Critical Illness Cover (2025)' : '-';
            newCI.details[1].value = values['illnessCover'] ? Helpers.toCurrency(Number(values['illnessCover'])) : '-';
            ciTarget = { ...newCI };
          }
          // set(ciTarget, 'details[0].value', values['illnessCover'] ? 'Critical Illness Cover (2025)': '-');
          // set(ciTarget, 'details[1].value', values['illnessCover'] ? Helpers.toCurrency(Number(values['illnessCover'])) : '-');
          // console.warn('illnessCover final', values['illnessCover'], { ciTarget });
        } else {
          if (paTarget) {
            ciTarget = paTarget;
            ciTarget['name'] = 'Add-ons';
          } else {
            ciTarget = newCI;
          }
          //console.warn('accidentCover', values['accidentCover']);
          set(ciTarget, 'details[0].value', values['accidentCover'] ? 'Personal Accident Cover': '-');
          set(ciTarget, 'details[1].value', values['accidentCover'] ? Helpers.toCurrency(Number(values['accidentCover'])) : '-');
        }
        newQuote = [...[basicTarget, { ...ciTarget } , otherTarget]];
      }      
    }


    if (name === 'sumAssured' && isEmpty(premium)) {
      const otherTarget = find(newQuote, { type: 'basic' });
      set(otherTarget, 'details[0].value', value ? 'Singlife Simple Term' : '-');
      set(otherTarget, 'details[1].value', value ? Helpers.toCurrency(Number(value)) : '-');
    }
    if (name === 'illnessCover' && isEmpty(premium)) {
      const otherTarget = find(newQuote, { type: 'add-on' });
      set(otherTarget, 'details[0].value', value ? 'Critical Illness Cover (2025)': '-');
      set(otherTarget, 'details[1].value', value ? Helpers.toCurrency(Number(value)) : '-');
    }
    // accident cover part
    if (name === 'accidentCover' && isEmpty(premium)) {
      if (!oneRider || !isCICover) {
        const otherTarget = find(newQuote, { type: 'add-on-pa' });
        set(otherTarget, 'details[0].value', value ? 'Personal Accident Cover': '-');
        set(otherTarget, 'details[1].value', value ? Helpers.toCurrency(Number(value)) : '-');        
      } else if (oneRider && !isCICover) {
        const otherTarget = find(newQuote, { type: 'add-on-pa' }) || find(newQuote, { type: 'add-on' });
        set(otherTarget, 'details[0].value', value ? 'Personal Accident Cover': '-');
        set(otherTarget, 'details[1].value', value ? Helpers.toCurrency(Number(value)) : '-');     
      }
    }
    
    if (isEmpty(premium)) {
      setQuotes([...newQuote]);
    }
    
  }

  const patchPremiumTerm = (value: string, target: any) => {
    const premiumTerm = Helpers.getPremiumTermByCode(value);
    const otherTarget = find(target, { type: 'others' });
    if (otherTarget) {
      set(otherTarget, 'details[0].value', premiumTerm);
    }
    return [...target];
  }

  const onChangeDropdown = (event: any) => {
    if (isEmpty(premium)) {
      const { value } = event.target;
      const nextQuotes = patchPremiumTerm(value, quotes);
      setQuotes(nextQuotes);
    }
    onChange(event);
    if (!needToCalculate) {
      setNeedToCalculate(true);
      setAppliedDiscounts([]);
    }
  }

  useEffect(() => {
    if (!basicDetails) {
      dispatch(completeStep(false));
      onBack();
    } else {

      if (!isPageLoaded) {
        if (quoteRawResponse) {
          console.log(quoteRawResponse);
          const pocket = new SQSPocketResponse(quoteRawResponse);
          setCalculatedPremium(pocket);
          patchValue({ promoCode: appliedPromoCode });
          setIsPageLoaded(true);

        } else {
          if (isEmpty(quotes)) {
            const planPremiumMode = get(initValue, 'planPremiumMode', 'AP');
            const nextQuotes = patchPremiumTerm(planPremiumMode, quoteInitialSample);
            setQuotes(nextQuotes);
            console.log(initValue, planPremiumMode, nextQuotes);
            setIsPageLoaded(true);
          }
          trackOnload('', null);

        }
        setNeedToCalculate(true);
      }
    }
  }, [quotes, initValue, isPageLoaded, setCalculatedPremium, quoteRawResponse, onBack, basicDetails, dispatch, appliedPromoCode, patchValue])

  const isShowAccidentRider = () => {
    return !(selectedOccupation?.status === 'Decline');
  }

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={1} label="Choose your coverage... " />
          {isCalculated && (
            <StickyBox><PremiumCost {...premium} /></StickyBox>
          )}
          <Heading margin="0 0 30px"> Choose your coverage </Heading>
        </FullGrid>
        <LeftGrid>

          <form onSubmit={e => { e.preventDefault() }} noValidate>
            <ContainerBlock padding={0} margin="0 0 40px" marginTablet="0 0 60px">
              <Heading variant="sub" fontWeight={600} margin="0 0 15px"> Singlife Simple Term - Decide the coverage you need </Heading>
              <Divider margin="0 0 30px" />
              <Text margin="0 0 5px">Typically, you should aim to have approximately 9-10 times your annual earnings as basic life cover, although this would vary from person to person.</Text>
              <Heading margin="0 0 30px" variant="desc">Source: Life Insurance Association</Heading>
              <Heading variant="label">Sum assured</Heading>
              <Text margin="0 0 15px 0">Please enter an amount between S$150,000 and S$500,000 (in multiples of S$1,000).</Text>
              {!errors?.sumAssured?.isValid && errors?.sumAssured?.message && (
                <Bullet variant="error">{errors?.sumAssured?.message}</Bullet>
              )}
              <Input name="sumAssured" invalid={!isEmpty(errors?.sumAssured?.message)} maxLength={9} type="numeric" value={values['sumAssured']} onChange={onChange} variant="prefix" boxLabel="S$"></Input>
              <Heading variant="label">Premium Payment Frequency</Heading>
              <Dropdown margin="0" name="planPremiumMode" value={values['planPremiumMode']} onChange={onChangeDropdown} source={paymentFrequency} bindId="code" bindLabel="description" placeholder="Please select" />
            </ContainerBlock>

            <Heading variant="sub" fontWeight={600} margin="0 0 15px"> Add-on coverage</Heading>
            <Divider margin="0 0 30px" />

            <ContainerBlock padding={20} paddingTablet={30} margin="0 0 30px" background={theme.colors.grey[200]}>
              <Heading variant="sub" margin="0 0 10px" fontWeight={600}> Critical Illness Cover (2025) </Heading>
              <TextCollapsible labelMore="Know more" labelLess="Know less" margin="0 0 30px">
                <Text margin="0 0 10px">Option to add on Critical Illness Cover (2025) to get a lump-sum payout upon diagnosis of any of the 36 covered critical illnesses so you can focus on recovery.</Text>
              </TextCollapsible>
              <Heading variant="label" margin="0 0 10px">Sum assured</Heading>
              <Text margin="0 0 15px">Please note: Critical Illness Cover (2025) rider Sum Assured has to be the same or less than Basic Plan and between S$50,000 to S$350,000 (In multiples of S$1,000).</Text>
              {!errors?.illnessCover?.isValid && errors?.illnessCover?.message && (
                <Bullet variant="error">{errors?.illnessCover?.message}</Bullet>
              )}
              <Input margin={0} name="illnessCover" invalid={!isEmpty(errors?.illnessCover?.message)} maxLength={9} type="numeric" value={values['illnessCover']} onChange={onChange} variant="prefix" boxLabel="S$"></Input>
            </ContainerBlock>
            {isShowAccidentRider() && (
              <ContainerBlock padding={20} paddingTablet={30} margin="0 0 40px"  marginTablet="0 0 60px" background={theme.colors.grey[200]}>
                <Heading variant="sub" margin="0 0 10px" fontWeight={600}> Personal Accident Cover </Heading>
                <TextCollapsible labelMore="Know more" labelLess="Know less" margin="0 0 30px">
                  <Text margin="0 0 10px">Option to add on Personal Accident Cover to get your medical bills due to an accident reimbursed and enjoy other benefits such as coverage for Traditional Chinese Medicine.</Text>
                </TextCollapsible>
                <Heading variant="label" margin="0 0 10px">Sum assured</Heading>
                <Text margin="0 0 15px">Please enter an amount between S$100,000 and S$500,000 (in multiples of S$50,000).</Text>
                {!errors?.accidentCover?.isValid && errors?.accidentCover?.message && (
                  <Bullet variant="error">{errors?.accidentCover?.message}</Bullet>
                )}
                <Input margin={0} name="accidentCover" maxLength={9} type="numeric" invalid={!isEmpty(errors?.accidentCover?.message)} value={values['accidentCover']} onChange={onChange} variant="prefix" boxLabel="S$"></Input>
              </ContainerBlock>
            )}
            <Heading variant="sub" fontWeight={600} margin="0 0 15px"> Check for promos and discounts</Heading>
            <Divider margin="0 0 30px" />
            <Heading variant="label">Enter your promo code</Heading>
            {isEmpty(isEmpty) && isAppliedPromoCode && <Bullet variant="success"> Promo code is successfully applied. </Bullet>}
            {!isEmpty(promoCodeError) && <Bullet variant="error"> {promoCodeError} </Bullet>}
            <FlexBox>
              <Input name="promoCode" textTransform="uppercase" maxLength={6} value={values['promoCode']} invalid={!isEmpty(promoCodeError)} onChange={onChange} margin="0 0 20px"></Input>
              <Button disabled={isNil(values['promoCode']) || isEmpty(values['promoCode']) || !errors?.sumAssured?.isValid || isAppliedPromoCode} onClick={onCheckPromoCode} margin="0" label="Check now"></Button>
            </FlexBox>

            {
              !isEmpty(appliedDiscounts) && <BulletList margin="0 0 40px">
                {appliedDiscounts.map(discount => (<Bullet>
                  {discount}
                </Bullet>))}
              </BulletList>
            }
            <BeforeProcessWarning
              insuranceEstimatorUrl={insuranceEstimatorUrl}
              budgetCalculatorUrl={budgetCalculatorUrl}
              compareFirstUrl={compareFirstUrl}
              totalPremium={premium?.totalAnnualizedPremium}
              isShowExplaination={isCalculated}
            />
          </form>

          {needToCalculate ?
            <Button margin="0 0 20px" disabled={!formValid || (!isEmpty(values['promoCode']) && !isAppliedPromoCode)} label="Calculate your premium" onClick={onCalculatePremium} /> :
            <Button margin="0 0 20px" disabled={!formValid} label="Continue" onClick={toggleModal} />
          }
          <Button margin="0 0 40px" variant="secondary" label="Back" onClick={onBack} />
        </LeftGrid>

        <EmptyGrid></EmptyGrid>

        <RightGrid>
          <Quote details={quotes} />
          {isCalculated && <PolicyDocument url={productSummary} />}
          <ContactBox items={contactInformation} />
          <Modal show={isShowConfirm} onClose={toggleModal}>
            <Heading variant="smallTitle" fontWeight={600} margin="0 0 20px" marginTablet="0 0 30px">Please confirm...</Heading>
            <Heading variant="label" fontWeight="normal" margin="0 0 15px" marginTablet="0 0 10px">Do you meet at least two of the following conditions?</Heading>
            <BulletList margin="0 0 30px">
              <Bullet><Text>You’re below 62 years old.</Text></Bullet>
              <Bullet><Text>You’re proficient in both spoken and written English.</Text></Bullet>
              <Bullet><Text>You’ve attained at least a GCE N-Level or O-Level certification, or equivalent academic qualification.</Text></Bullet>
            </BulletList>
            <Button margin="0 0 20px" label="Yes" onClick={onContinue} />
            <Button variant="secondary" label="No" margin={0} onClick={() => { gotoContact(true) }} />
          </Modal>
        </RightGrid>
      </ContainerGrid>

    </Container>
  );
};

export default Coverage;
